import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { EVAL_CELL_CLS, EVAL_HEAD_CLS } from "./EvalTable";
import { EVAL_CHART_HEIGHT_CLS } from "./constants/charts";

/**
 * What the dashboard shows while its data is in flight.
 *
 * Placeholders rather than the real components fed empty data: every one of those
 * has a legitimate empty state that states a finding — "no judged runs in this
 * window yet", a flaky count of zero — and against data that has merely not
 * arrived those are false in the reassuring direction. A placeholder holds the
 * geometry through all three waves of requests instead.
 */

/**
 * One cell still in flight, in a table whose other columns have arrived: a run's
 * identity comes from a different request than its outcome, and those columns'
 * genuine empty values would read as a run that measured nothing.
 */
export const CellSkeleton = ({ className }: { className?: string }) => (
  <Skeleton className={cn("h-3 w-10", className)} />
);

/** A plot area at the charts' own height, so the card never changes size. */
export const ChartSkeleton = () => (
  <Skeleton className={EVAL_CHART_HEIGHT_CLS} role="status" aria-label="Loading chart" />
);

interface TableSkeletonProps {
  /**
   * One Tailwind width class per column, in order. Approximate by design: enough
   * that the placeholder does not imply a shape the real header contradicts.
   */
  columns: string[];
  rows?: number;
}

/**
 * A table's rows while they load, at the row pitch `EvalTable` sets. Unlabelled:
 * repeating the header text would put the column names in two places to drift.
 */
export const TableSkeleton = ({ columns, rows = 6 }: TableSkeletonProps) => (
  <div role="status" aria-label="Loading table">
    <div className={cn(EVAL_HEAD_CLS, "flex items-center gap-6 border-b border-border")}>
      {columns.map((width, column) => (
        <Skeleton key={column} className={cn("h-3", width)} />
      ))}
    </div>
    {Array.from({ length: rows }, (_, index) => (
      <div
        key={index}
        className={cn(EVAL_CELL_CLS, "flex items-center gap-6 border-b border-border")}
      >
        {columns.map((width, column) => (
          <Skeleton key={column} className={cn("h-4", width)} />
        ))}
      </div>
    ))}
  </div>
);
